/**
 * Loop principal de reprodução.
 *
 * - Mantém um ponteiro na fila de items
 * - Aguarda o renderer sinalizar fim da exibição (promise)
 * - Enfileira play log de forma não-bloqueante
 * - Recompila a fila ao receber novo schedule
 * - A cada avanço, recompila a partir do schedule em cache (dayporating por hora do item
 *   muda ao longo do dia — sem recompile, itens 02:00–02:10 ficam fora da fila para sempre)
 * - Pula items com cap local atingido
 * - Pula items em cooldown de falha (playback-failure-tracker); com todos em cooldown, mostra o
 *   fallback e agenda a retentativa em vez de girar a fila sem pausa
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { localCounters, type SkipReason } from '@/engine/local-counters'
import { playLogManager } from '@/engine/play-log-manager'
import { playbackFailureKey, playbackFailureTracker } from '@/engine/playback-failure-tracker'
import { measurePlayback } from '@/engine/playback-measurement'
import { compile } from '@/engine/playlist-compiler'
import { scheduleManager } from '@/engine/schedule-manager'
import { useTriggerWatcher } from '@/hooks/use-trigger-watcher'
import { deviceService } from '@/services/device.service'
import { useAuthStore } from '@/store/auth.store'
import type { CompiledSlotDTO, PlaybackResult, PlayQueueItem } from '@/types/player.types'
import { playerLogger } from '@/utils/player-logger'

interface UsePlayLoopResult {
  currentItem: PlayQueueItem | null
  /**
   * Incrementa a cada item posto em tela. Entra na key do renderer: dois itens seguidos com o mesmo
   * slot/asset (fila de 1 item, repetição de SOV) precisam remontar, senão os efeitos do renderer não
   * rodam de novo, `onEnd` nunca mais é chamado e o player congela.
   */
  playSeq: number
  /** `result` ausente (renderer legado) = exibido, com tempo medido desde a entrada do item. */
  onItemEnded: (result?: PlaybackResult) => void
  triggerRecompile: (version: string) => void
}

export function usePlayLoop(): UsePlayLoopResult {
  const [currentItem, setCurrentItem] = useState<PlayQueueItem | null>(null)
  const [playSeq, setPlaySeq] = useState(0)
  const indexRef = useRef(0)
  const queueRef = useRef<PlayQueueItem[]>([])
  const playingRef = useRef(false)
  const recompileRef = useRef<(() => void) | null>(null)
  const retryTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  /** Retentativa única do modo degradado (todos os itens em cooldown de falha). */
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Já em modo degradado — o erro vai ao Sentry uma vez por episódio, não a cada retentativa. */
  const degradedRef = useRef(false)
  /** Epoch ms em que o item atual entrou em tela — fallback de medição para renderers legados. */
  const itemStartedAtRef = useRef(Date.now())
  const [activeSlots, setActiveSlots] = useState<CompiledSlotDTO[]>([])

  /** Inscreve nos campos específicos do store que trigam recompile (ex.: fuso vindo do heartbeat). */
  const workspaceTimezone = useAuthStore((s) => s.credentials?.workspaceTimezone ?? 'UTC')
  const workspaceName = useAuthStore((s) => s.credentials?.workspaceName ?? '')

  const { isTriggerSatisfied } = useTriggerWatcher(activeSlots)

  const compileQueue = useCallback(() => {
    const cache = scheduleManager.getCurrent()
    if (!cache || cache.slots.length === 0) {
      queueRef.current = []
      return
    }

    const queue = compile(
      cache.slots,
      workspaceTimezone,
      cache.version,
      workspaceName,
      workspaceName,
      scheduleManager.getCacheAgeSeconds() > 900,
      scheduleManager.getCacheAgeSeconds()
    )

    queueRef.current = queue
    setActiveSlots(cache.slots)

    playerLogger.debug('[usePlayLoop] Compiled queue, items:', queueRef)
  }, [workspaceTimezone, workspaceName])

  const advance = useCallback(() => {
    compileQueue()
    const queue = queueRef.current
    if (queue.length === 0) {
      setCurrentItem(null)
      playingRef.current = false
      return
    }

    if (indexRef.current >= queue.length) {
      indexRef.current = 0
    }

    const timezone = useAuthStore.getState().credentials?.workspaceTimezone ?? 'UTC'
    let attempts = 0
    let item: PlayQueueItem | null = null

    const show = (next: PlayQueueItem) => {
      // Retentativas pendentes (cooldown de falha, minInterval) não podem disparar advance() no meio
      // do item que entra agora.
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current)
        cooldownTimerRef.current = null
      }
      for (const timer of retryTimersRef.current) clearTimeout(timer)
      retryTimersRef.current = []
      itemStartedAtRef.current = Date.now()
      setCurrentItem(next)
      setPlaySeq((seq) => seq + 1)
      playingRef.current = true
    }

    // F-24: um screen_off ativo (com trigger satisfeito, se houver) desliga a tela — vence qualquer conteúdo,
    // inclusive de prioridade maior; é o significado de "tela desligada" na agenda.
    const screenOff = queue.find(
      (candidate) =>
        candidate.eventAction === 'screen_off' && (!candidate.triggerRules || isTriggerSatisfied(candidate.slotId))
    )
    if (screenOff) {
      show(screenOff)
      return
    }

    // Primeira passagem: busca o próximo item válido na ordem sequencial.
    // Slots bloqueados apenas por minInterval são registrados para reschedule.
    let earliestIntervalExpiry = Infinity
    const intervalOnlyBlocked: PlayQueueItem[] = []
    const coolingDown: PlayQueueItem[] = []

    while (attempts < queue.length) {
      const candidate = queue[indexRef.current % queue.length]
      indexRef.current++

      if (candidate.eventAction === 'screen_off') {
        attempts++
        continue
      }

      const reason: SkipReason = localCounters.skipReason(candidate, timezone)

      if (reason === 'cap') {
        attempts++
        continue
      }

      if (reason === 'interval') {
        // Registra para poder priorizar assim que o cooldown expirar
        intervalOnlyBlocked.push(candidate)
        const last = localCounters.getLastPlayed(candidate.slotId)
        const expiresAt = (last ?? 0) + (candidate.minIntervalSeconds ?? 0) * 1000
        if (expiresAt < earliestIntervalExpiry) earliestIntervalExpiry = expiresAt
        attempts++
        continue
      }

      if (candidate.triggerRules && !isTriggerSatisfied(candidate.slotId)) {
        attempts++
        continue
      }

      if (playbackFailureTracker.isCoolingDown(playbackFailureKey(candidate))) {
        coolingDown.push(candidate)
        attempts++
        continue
      }

      item = candidate
      break
    }

    // Segunda passagem: se não achou nada na ordem sequencial mas há slots aguardando
    // apenas cooldown, agenda uma retentativa para quando o mais próximo expirar
    // (tolerância máxima de 60s conforme contrato da feature).
    if (!item && intervalOnlyBlocked.length > 0) {
      const msUntilExpiry = earliestIntervalExpiry - Date.now()
      const delay = Math.max(0, Math.min(msUntilExpiry, 60_000))
      const timer = setTimeout(() => {
        retryTimersRef.current = retryTimersRef.current.filter((t) => t !== timer)
        // Reposiciona o ponteiro para o slot de intervalo mais próximo e força advance
        const idx = queue.findIndex((q) => q.slotId === intervalOnlyBlocked[0].slotId)
        if (idx !== -1) indexRef.current = idx
        advance()
      }, delay)
      retryTimersRef.current.push(timer)
      return
    }

    // Modo degradado: tudo que poderia tocar está em cooldown de falha. Mostra o fallback e agenda UMA
    // retentativa para quando o primeiro cooldown vencer — antes, o loop girava a fila sem pausa.
    if (!item && coolingDown.length > 0) {
      setCurrentItem(null)
      playingRef.current = false
      if (!degradedRef.current) {
        degradedRef.current = true
        playerLogger.error('[usePlayLoop] Todos os itens reproduzíveis estão falhando; modo degradado', {
          failingItems: coolingDown.length,
          assetIds: coolingDown.map((c) => c.assetId ?? c.slotId).slice(0, 10)
        })
      }
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current)
      const retryAt = playbackFailureTracker.earliestRetryAt()
      const delay = Math.max(1_000, (retryAt ?? Date.now()) - Date.now())
      cooldownTimerRef.current = setTimeout(() => {
        cooldownTimerRef.current = null
        advance()
      }, delay)
      return
    }

    if (!item) {
      compileQueue()
      setCurrentItem(null)
      playingRef.current = false
      return
    }

    show(item)
  }, [compileQueue, isTriggerSatisfied])

  const onItemEnded = useCallback(
    (result?: PlaybackResult) => {
      const item = currentItem
      const creds = useAuthStore.getState().credentials
      if (!item || !creds) return

      // F-07: só gera proof-of-play (e leilão/contador local) se algo de fato foi exibido.
      const measurement = measurePlayback(item.durationSeconds, result, itemStartedAtRef.current)
      const failureKey = playbackFailureKey(item)
      if (!measurement) {
        // screen_off reporta played:false por design — não é falha de mídia.
        if (result?.played === false && item.eventAction !== 'screen_off') {
          playbackFailureTracker.recordFailure(failureKey)
        }
        advance()
        return
      }
      playbackFailureTracker.recordSuccess(failureKey)
      // O episódio degradado só termina quando algo de fato toca — retentativas que falham de novo não
      // geram um evento novo no Sentry.
      degradedRef.current = false

      const enqueue = async (): Promise<void> => {
        let auctionFields:
          | { auctionId?: string; listingId?: string; grantId?: string; guestCampaignId?: string }
          | undefined

        if (item.listingId && item.guestCampaignId) {
          try {
            const auction = await deviceService.createMarketplaceAuction(
              creds.deviceId,
              item.listingId,
              item.guestCampaignId
            )
            auctionFields = {
              auctionId: auction.type === 'won' ? auction.auctionId : undefined,
              listingId: auction.type === 'won' ? auction.listingId : item.listingId,
              grantId: auction.type === 'won' ? auction.grantId : item.grantId,
              guestCampaignId: auction.type === 'won' ? auction.guestCampaignId : item.guestCampaignId
            }
          } catch {
            auctionFields = {
              auctionId: item.auctionId,
              listingId: item.listingId,
              grantId: item.grantId,
              guestCampaignId: item.guestCampaignId
            }
          }
        }

        playLogManager.enqueue({
          deviceId: creds.deviceId,
          workspaceId: creds.workspaceId,
          auctionId: auctionFields?.auctionId ?? item.auctionId,
          listingId: auctionFields?.listingId ?? item.listingId,
          grantId: auctionFields?.grantId ?? item.grantId,
          slotId: item.guestCampaignId ? undefined : item.slotId,
          campaignId: item.campaignId,
          guestCampaignId: auctionFields?.guestCampaignId ?? item.guestCampaignId,
          playlistItemId: item.playlistItemId,
          campaignCreativeId: item.guestCampaignId ? undefined : item.creativeId,
          guestCampaignCreativeId: item.guestCampaignId ? item.creativeId : undefined,
          assetId: item.assetId,
          durationSeconds: measurement.durationSeconds,
          playedAt: measurement.playedAt,
          contextSnapshot:
            result?.contentVerified === undefined
              ? item.contextSnapshot
              : { ...item.contextSnapshot, contentVerified: result.contentVerified }
        })
      }

      const timezone = creds.workspaceTimezone ?? 'UTC'
      void enqueue()
      localCounters.record(item.slotId, timezone)
      advance()
    },
    [currentItem, advance]
  )

  const triggerRecompile = useCallback(
    (_version: string) => {
      compileQueue()

      if (!playingRef.current) {
        indexRef.current = 0
        advance()
      }
    },
    [compileQueue, advance]
  )

  recompileRef.current = () => triggerRecompile('')

  // Reage a mudança de timezone/workspace (ex.: fuso vindo do heartbeat).
  // compileQueue muda de referência quando workspaceTimezone ou workspaceName mudam,
  // então o efeito re-executa automaticamente sem precisar de credentials nas deps.
  useEffect(() => {
    if (playingRef.current) {
      compileQueue()
    } else {
      void advance()
    }
  }, [compileQueue, advance])

  // Com fila vazia, “entrar” na janela de dayporating (ex. 02:00) não gera nenhum evento
  // — recheca a fila 1×/min quando não está a reproduzir.
  useEffect(() => {
    const t = setInterval(() => {
      if (playingRef.current) return
      compileQueue()
      if (queueRef.current.length > 0) void advance()
    }, 60_000)
    return () => clearInterval(t)
  }, [compileQueue, advance])

  useEffect(() => {
    return () => {
      for (const timer of retryTimersRef.current) clearTimeout(timer)
      retryTimersRef.current = []
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current)
      cooldownTimerRef.current = null
    }
  }, [])

  return { currentItem, playSeq, onItemEnded, triggerRecompile }
}
